/**
 * AuditLogPage - View audit logs for compliance and traceability
 * Available to SUPERVISOR+ roles
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ACCESS_TOKEN_KEY = 'riskee_access_token';

const getAuthHeaders = () => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Event type badge colors
const EVENT_TYPE_COLORS = {
  'CREATE': { bg: '#ecfdf5', color: '#10b981' },
  'UPDATE': { bg: '#eff6ff', color: '#3b82f6' },
  'DELETE': { bg: '#fef2f2', color: '#ef4444' },
  'TRANSITION': { bg: '#f5f3ff', color: '#8b5cf6' },
  'VERIFY': { bg: '#ecfeff', color: '#06b6d4' },
  'COMPLETE': { bg: '#ecfdf5', color: '#10b981' },
  'LOGIN': { bg: '#fefce8', color: '#eab308' },
};

// Entity type labels
const ENTITY_LABELS = {
  'RISK': 'Report',
  'ACTION': 'Action',
  'INSPECTION': 'Inspection',
  'SITE': 'Site',
  'USER': 'User',
  'INCIDENT': 'Incident',
  'DG': 'Dangerous Goods',
  'FIRE_SAFETY': 'Fire Safety',
  'EQUIPMENT': 'Equipment',
  'WELFARE': 'Welfare',
  'INJURY_CLAIM': 'Injury Claim',
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    entity_type: '',
    event_type: '',
    days: 30
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.event_type) params.set('event_type', filters.event_type);
      params.set('days', filters.days);
      params.set('limit', 100);
      
      const res = await fetch(`${API}/audit-logs?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('You do not have permission to view audit logs');
        }
        throw new Error(`Failed to load audit logs: ${res.status}`);
      }
      
      const data = await res.json();
      setLogs(data.items || []);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="page audit-log-page" data-testid="audit-log-page">
      <div className="page-header">
        <div className="page-header-content">
          <h2>Audit Log</h2>
          <p className="page-subtitle">
            Track all changes and actions for compliance and traceability
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs}>
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="filter-group">
            <label className="filter-label">Entity Type</label>
            <select 
              className="filter-select"
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
              data-testid="filter-entity-type"
            >
              <option value="">All Types</option>
              {Object.entries(ENTITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Event Type</label>
            <select 
              className="filter-select"
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              data-testid="filter-event-type"
            >
              <option value="">All Events</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="TRANSITION">Transition</option>
              <option value="VERIFY">Verify</option>
              <option value="COMPLETE">Complete</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Time Period</label>
            <select 
              className="filter-select"
              value={filters.days}
              onChange={(e) => handleFilterChange('days', parseInt(e.target.value))}
              data-testid="filter-days"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading audit logs...</p>
        </div>
      ) : error ? (
        <div className="error-state" data-testid="audit-error">
          <p className="error-message">{error}</p>
          <Button variant="outline" onClick={fetchLogs}>Try Again</Button>
        </div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <p>No audit logs found for the selected filters.</p>
        </div>
      ) : (
        <div className="card">
          <table className="data-table" data-testid="audit-log-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Entity</th>
                <th>Event</th>
                <th>Summary</th>
                <th>Actor</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const eventStyle = EVENT_TYPE_COLORS[log.event_type] || { bg: '#f3f4f6', color: '#6b7280' };
                const entityLabel = ENTITY_LABELS[log.entity_type] || log.entity_type;
                
                return (
                  <tr key={log.id} data-testid={`audit-row-${log.id}`}>
                    <td className="timestamp-cell">
                      {formatDateTime(log.occurred_at)}
                    </td>
                    <td>
                      <div className="entity-cell">
                        <span className="entity-type">{entityLabel}</span>
                        <span className="entity-id">{log.entity_id?.substring(0, 8)}...</span>
                      </div>
                    </td>
                    <td>
                      <span 
                        className="event-badge"
                        style={{ 
                          backgroundColor: eventStyle.bg, 
                          color: eventStyle.color 
                        }}
                      >
                        {log.event_type}
                      </span>
                    </td>
                    <td className="summary-cell">
                      {log.summary}
                    </td>
                    <td className="actor-cell">
                      {log.actor_name || 'System'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .audit-log-page .page-subtitle {
          color: var(--text-muted);
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }
        
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .filter-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .filter-select {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.875rem;
          min-width: 140px;
        }
        
        .timestamp-cell {
          font-size: 0.8125rem;
          color: var(--text-muted);
          white-space: nowrap;
        }
        
        .entity-cell {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }
        
        .entity-type {
          font-weight: 600;
          font-size: 0.8125rem;
        }
        
        .entity-id {
          font-size: 0.75rem;
          color: var(--text-muted);
          font-family: monospace;
        }
        
        .event-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .summary-cell {
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 0.875rem;
        }
        
        .actor-cell {
          font-size: 0.8125rem;
          color: var(--text-secondary);
        }
        
        .loading-state, .error-state, .empty-state {
          text-align: center;
          padding: 3rem;
          color: var(--text-muted);
        }
        
        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          margin: 0 auto 1rem;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuditLogPage;
